/**
 * AUDIO PAGE catalog (pure data + gain-mapping helpers, no Web Audio). Drives the
 * standalone /audio.html browser: the 5 music tracks, the sampled + procedural SFX
 * registries, the 5 mix buses, and the per-category source/licence notes. Kept free
 * of side effects so it is unit-testable without an AudioContext.
 */
import type { MusicContext, MusicTrackId } from './musicManager.js';
import type { SfxEvent, SfxLoopId } from './sfxSamples.js';
import type { AudioSettings } from '../settings/types.js';

// Numeric mix buses only — the boolean flags (muted, radioFilter) are not faders.
export type BusKey = keyof Omit<AudioSettings, 'muted' | 'radioFilter'>;
export type AudioSource = 'elevenlabs' | 'procedural' | 'voice';

/** A procedural one-shot exposed by AudioEngine — no-arg trigger methods only. */
export type ProceduralMethod =
  | 'boost' | 'fire' | 'hit' | 'incomingHit' | 'kill' | 'dock'
  | 'uiClick' | 'death' | 'lockAcquired' | 'waveInbound'
  | 'recordTruth' | 'recordContradiction';

export interface MusicTrackInfo {
  id: MusicTrackId;
  context: MusicContext;
  label: string;
  file: string;
  note: string;
}

export interface SampledSfxInfo {
  /** SfxEvent for one-shots; the two engine loops are driven by the throttle slider. */
  event: SfxEvent | null;
  label: string;
  file: string;
  bus: 'sfx' | 'ui' | 'ambience';
  note: string;
}

/** A named looping cue (supercruise tunnel bed / hull-critical alarm). */
export interface SampledLoopInfo {
  id: SfxLoopId;
  label: string;
  file: string;
  bus: 'sfx' | 'ui' | 'ambience';
  note: string;
}

export interface ProceduralSfxInfo {
  method: ProceduralMethod;
  label: string;
  bus: BusKey;
  /** False when AudioEngine prefers a sampled buffer and only falls back to the tone. */
  pure: boolean;
}

export interface BusInfo {
  key: BusKey;
  label: string;
  note: string;
}

export const MUSIC_TRACKS: readonly MusicTrackInfo[] = [
  { id: 'music-menu', context: 'menu', label: 'Menu', file: 'music-menu.mp3', note: 'Title / pause — the cold open' },
  { id: 'music-docked', context: 'docked', label: 'Docked', file: 'music-docked.mp3', note: 'Station berth — calm bed while docked' },
  { id: 'music-flight', context: 'flight', label: 'Flight', file: 'music-flight.mp3', note: 'Open-space cruise bed' },
  { id: 'music-tension', context: 'tension', label: 'Tension', file: 'music-tension.mp3', note: 'Combat active' },
  { id: 'music-cenotaph', context: 'cenotaph', label: 'Cenotaph', file: 'music-cenotaph.mp3', note: 'Near a cenotaph / verifying' },
];

export const SAMPLED_SFX: readonly SampledSfxInfo[] = [
  { event: null, label: 'Engine idle', file: 'sfx/engine-idle.mp3', bus: 'ambience', note: 'Loop — drive with the throttle demo' },
  { event: null, label: 'Engine thrust', file: 'sfx/engine-thrust.mp3', bus: 'ambience', note: 'Loop — drive with the throttle demo' },
  { event: 'boost', label: 'Boost', file: 'sfx/boost.mp3', bus: 'sfx', note: 'Afterburner kick' },
  { event: 'fire', label: 'Weapon fire', file: 'sfx/weapon-fire.mp3', bus: 'sfx', note: 'Primary cannon' },
  { event: 'impact', label: 'Impact', file: 'sfx/impact.mp3', bus: 'sfx', note: 'Hull hit landed' },
  { event: 'shieldHit', label: 'Shield hit', file: 'sfx/shield-hit.mp3', bus: 'sfx', note: 'Incoming hit absorbed' },
  { event: 'dock', label: 'Dock', file: 'sfx/dock.mp3', bus: 'sfx', note: 'Docking clamp engaged' },
  // Wave 4 gated cues — absent today, degrade to silence / procedural fallback.
  { event: 'verifyTruth', label: 'Verify · truth', file: 'sfx/verify-truth.mp3', bus: 'ui', note: 'VERIFY resolves TRUTH (falls back to tone)' },
  { event: 'verifyContradiction', label: 'Verify · contradiction', file: 'sfx/verify-contradiction.mp3', bus: 'ui', note: 'CONTRADICTION amber→red (falls back to tone)' },
  { event: 'cruiseSpool', label: 'Cruise spool', file: 'sfx/cruise-spool.mp3', bus: 'sfx', note: 'Supercruise spool riser (silence if absent)' },
  { event: 'cruiseEngage', label: 'Cruise engage', file: 'sfx/cruise-engage.mp3', bus: 'sfx', note: 'Supercruise engage boom (silence if absent)' },
  { event: 'cruiseDrop', label: 'Cruise drop', file: 'sfx/cruise-drop.mp3', bus: 'sfx', note: 'Supercruise safe-drop (silence if absent)' },
  { event: 'deathDriftBed', label: 'Death drift bed', file: 'sfx/death-drift-bed.mp3', bus: 'ambience', note: 'Dead-stick drift — layers under the 130 Hz tone' },
  { event: 'uiTick', label: 'UI tick', file: 'sfx/ui-tick.mp3', bus: 'ui', note: 'Menu hover tick (silence if absent)' },
  { event: 'uiConfirm', label: 'UI confirm', file: 'sfx/ui-confirm.mp3', bus: 'ui', note: 'Menu confirm (falls back to UI click)' },
  { event: 'uiBack', label: 'UI back', file: 'sfx/ui-back.mp3', bus: 'ui', note: 'Menu back (silence if absent)' },
  { event: 'uiDenied', label: 'UI denied', file: 'sfx/ui-denied.mp3', bus: 'ui', note: 'Denied action (silence if absent)' },
  { event: 'killConfirm', label: 'Kill confirm', file: 'sfx/kill-confirm.mp3', bus: 'sfx', note: 'Kill confirmed (falls back to tone)' },
  { event: 'lockAcquiredV2', label: 'Lock acquired', file: 'sfx/lock-acquired-v2.mp3', bus: 'ui', note: 'Target lock (falls back to tone)' },
  { event: 'waveInboundV2', label: 'Wave inbound', file: 'sfx/wave-inbound-v2.mp3', bus: 'ui', note: 'Wave klaxon (falls back to tone)' },
  { event: 'scoopCapture', label: 'Scoop capture', file: 'sfx/scoop-capture.mp3', bus: 'sfx', note: 'Salvage scooped (falls back to UI click)' },
  { event: 'undockClamp', label: 'Undock clamp', file: 'sfx/undock-clamp.mp3', bus: 'sfx', note: 'Undock clamp release (falls back to tone)' },
];

/** Named looping cues, start/stop-controlled (not one-shots). Silent when absent. */
export const SAMPLED_LOOPS: readonly SampledLoopInfo[] = [
  { id: 'cruiseTunnel', label: 'Cruise tunnel', file: 'sfx/cruise-tunnel-loop.mp3', bus: 'ambience', note: 'Supercruise tunnel bed (loop)' },
  { id: 'hullCritical', label: 'Hull critical', file: 'sfx/hull-critical-loop.mp3', bus: 'sfx', note: 'Hull-critical alarm (loop)' },
];

export const PROCEDURAL_SFX: readonly ProceduralSfxInfo[] = [
  { method: 'boost', label: 'Boost', bus: 'sfx', pure: false },
  { method: 'fire', label: 'Fire', bus: 'sfx', pure: false },
  { method: 'hit', label: 'Hit', bus: 'sfx', pure: false },
  { method: 'incomingHit', label: 'Incoming hit', bus: 'sfx', pure: false },
  { method: 'dock', label: 'Dock', bus: 'ui', pure: false },
  // Wave 4: these now try a sampled cue first, so the tone is a fallback (impure).
  { method: 'kill', label: 'Kill', bus: 'sfx', pure: false },
  { method: 'uiClick', label: 'UI click', bus: 'ui', pure: true },
  { method: 'death', label: 'Death', bus: 'ambience', pure: true },
  { method: 'lockAcquired', label: 'Lock acquired', bus: 'ui', pure: false },
  { method: 'waveInbound', label: 'Wave inbound', bus: 'ui', pure: false },
  { method: 'recordTruth', label: 'Record truth', bus: 'ui', pure: false },
  { method: 'recordContradiction', label: 'Record contradiction', bus: 'ui', pure: false },
];

export const MIX_BUSES: readonly BusInfo[] = [
  { key: 'master', label: 'Master', note: 'Mute folds this to silence' },
  { key: 'music', label: 'Music', note: 'Drives the 5 score tracks' },
  { key: 'sfx', label: 'SFX', note: 'Weapons / impacts / boost' },
  { key: 'ui', label: 'UI', note: 'Clicks / locks / ledger beats' },
  { key: 'ambience', label: 'Ambience', note: 'Engine hum + loops' },
];

export const SOURCE_NOTES: Record<AudioSource, string> = {
  elevenlabs: 'Music + sampled SFX — AuthHub ElevenLabs generated (.mp3).',
  procedural: 'Procedural SFX — synthesised live in the Web Audio API (no asset file).',
  voice: 'Campaign voice-over — AuthHub ElevenLabs generated per cast speaker (.mp3).',
};

/**
 * CAMPAIGN VO: maps a campaign speaker to its voice-asset filename slug. Cast
 * speakers resolve to a clip; environmental-narration speakers (PANEL 4-771,
 * SLATE, LANE FOUR, LOCKER FOUR, LEDGER) and anything unmapped have no cast
 * voice and are absent here, so they fall through to text-only (null) below.
 */
const VOICE_SLUGS: Readonly<Record<string, string>> = {
  VEY: 'vey',
  JEM: 'jem',
  HALIS: 'halis',
  KÍRA: 'kira',
  'PA VOICE': 'pa',
  'GUILD NOTICE': 'guild',
  UNKNOWN: 'vesper',
};

/**
 * Pure resolver for an Act 0-1 voice-over clip URL. Returns the public asset path
 * `'/audio/voice/<slug>-<commsId>-<NN>.mp3'` for a voiced cast speaker, or null for
 * environmental-narration / unmapped speakers (text-only). `NN` is the zero-padded
 * line index, matching the generated filenames (e.g. vey-c-cold-open-01.mp3).
 *
 * `explicitSlug` (a line's `voice` field) overrides the index-derived name and maps
 * straight to `'/audio/voice/<explicitSlug>.mp3'`. This keeps already-generated clips
 * pinned to their own line when a line is inserted mid-script (which would otherwise
 * shift every later line's index-derived filename) and lets inserts carry non-numeric
 * suffixes (e.g. `jem-c-kira-00a`). No side effects — safe to unit test without audio.
 */
export const voiceAssetFor = (
  commsId: string,
  lineIndex: number,
  speaker: string,
  explicitSlug?: string,
): string | null => {
  if (explicitSlug) return `/audio/voice/${explicitSlug}.mp3`;
  const slug = VOICE_SLUGS[speaker];
  if (!slug) return null;
  const nn = String(lineIndex).padStart(2, '0');
  return `/audio/voice/${slug}-${commsId}-${nn}.mp3`;
};

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

/** Slider integer percent (0..100) -> linear bus gain (0..1). */
export const percentToGain = (percent: number): number => clamp01(percent / 100);

/** Linear bus gain (0..1) -> rounded slider percent (0..100). */
export const gainToPercent = (gain: number): number => Math.round(clamp01(gain) * 100);
